import { useEffect, useState } from "react";
import Lenis from "lenis";
import { BrowserRouter, Route, Routes } from "react-router-dom";
import { Toaster } from "sonner";
import { ArrowRight } from "lucide-react";
import Nav from "@/components/site/Nav";
import Hero from "@/components/site/Hero";
import Marquee from "@/components/site/Marquee";
import Manifesto from "@/components/site/Manifesto";
import Estimator from "@/components/site/Estimator";
import HowItWorks from "@/components/site/HowItWorks";
import OwnerQuote from "@/components/site/OwnerQuote";
import LandClearing from "@/components/site/LandClearing";
import QuoteForm from "@/components/site/QuoteForm";
import Faq from "@/components/site/Faq";
import Footer from "@/components/site/Footer";
import Inbox from "@/components/site/Inbox";
import QuotePage from "@/components/site/QuotePage";
import AcceptQuote from "@/components/site/AcceptQuote";
import { PaymentSuccess, PaymentCancel } from "@/components/site/PaymentResult";
import SeoManager from "@/components/site/Seo";
import Decision from "@/components/site/Decision";
import BusinessCard from "@/components/site/BusinessCard";

function StumpGrinding({ widths, setWidths }) {
  return (
    <>
      <Nav />
      <a
        href="/land-clearing"
        data-testid="floating-clearing-link"
        className="fixed right-8 top-24 z-40 hidden items-center gap-2 border border-amber-500/60 bg-[#0c0a09]/90 px-4 py-2.5 font-mono text-[11px] font-semibold uppercase tracking-[0.15em] text-amber-500 shadow-[0_8px_30px_rgba(0,0,0,0.6)] backdrop-blur-md transition-colors duration-150 hover:bg-amber-500 hover:text-[#0c0a09] sm:flex"
      >
        Land clearing this way
        <ArrowRight className="h-3.5 w-3.5" />
      </a>
      <a
        href="/land-clearing"
        data-testid="mobile-clearing-link"
        className="mx-4 mt-3 flex items-center justify-center gap-2 border border-amber-500/60 bg-[#0c0a09]/90 px-4 font-mono text-[11px] font-semibold uppercase tracking-[0.15em] text-amber-500 transition-colors duration-150 hover:bg-amber-500 hover:text-[#0c0a09] sm:hidden"
        style={{ minHeight: 44 }}
      >
        Land clearing this way
        <ArrowRight className="h-3.5 w-3.5" />
      </a>
      <main>
        <Hero />
        <Marquee />
        <Manifesto />
        <Estimator widths={widths} setWidths={setWidths} />
        <HowItWorks />
        <QuoteForm widths={widths} setWidths={setWidths} />
        <Faq />
        <OwnerQuote />
      </main>
      <Footer />
    </>
  );
}

function App() {
  const [stumpWidths, setStumpWidths] = useState([18]);

  useEffect(() => {
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return undefined;
    const lenis = new Lenis({ duration: 1.1, smoothWheel: true });
    window.__lenis = lenis;
    let raf;
    const loop = (time) => {
      lenis.raf(time);
      raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);
    return () => {
      cancelAnimationFrame(raf);
      lenis.destroy();
      window.__lenis = null;
    };
  }, []);

  return (
    <div className="min-h-screen bg-[#0c0a09] text-foreground">
      <div className="grain-overlay" aria-hidden="true" />
      <BrowserRouter>
        <SeoManager />
        <Routes>
          <Route path="/" element={<Decision />} />
          <Route path="/stump-grinding" element={<StumpGrinding widths={stumpWidths} setWidths={setStumpWidths} />} />
          <Route path="/inbox" element={<Inbox />} />
          <Route path="/q/:id" element={<QuotePage />} />
          <Route path="/accept/:id" element={<AcceptQuote />} />
          <Route path="/payment/success" element={<PaymentSuccess />} />
          <Route path="/payment/cancel" element={<PaymentCancel />} />
          <Route path="/land-clearing" element={<LandClearing />} />
          <Route path="/card" element={<BusinessCard />} />
        </Routes>
      </BrowserRouter>
      <Toaster theme="dark" position="bottom-right" />
    </div>
  );
}

export default App;
